import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, CalendarDays, PartyPopper, FolderKanban, FileText, MonitorPlay,
  Camera, MessageSquare, BellRing, BarChart3, Users, ShieldCheck,
  ExternalLink, Phone, ArrowRight, Check, X, Eye, KeyRound, Video, EyeOff,
  MonitorDown, Apple, Terminal, LayoutDashboard, Moon, Cloud, Server,
  Building2, Boxes, Settings2,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

/* ---------------------------------------------------------------------------
 * PALETTE — the same tokens as the static marketing page in frontend/index.html,
 * written as literal hexes rather than tailwind.config theme keys so the page
 * cannot fall out of colour when a dev server holds a stale config:
 *
 *   #0B1020 ink        body text        #0E1738 navy   dark bands
 *   #3A4363 ink-2      secondary text   #080C1A         footer
 *   #6B7391 ink-3      muted / mono     #F4F6FC mist   muted section fill
 *   #2349EE brand-600  primary blue     #E3E7F5 line   hairlines
 *   #E8A317 amber      accent           #0E9F6E green  ticks, "your network"
 *   #8FA6FF            eyebrows on navy #C7D2FF        code block text
 * ------------------------------------------------------------------------- */

/* ---------------------------------------------------------------------------
 * EDIT ME — the sales contact this page routes every primary enquiry to.
 * "Start free trial", "Open the live demo" and "Request a demo" open
 * `contactUrl` in a new tab. `phone` is what the tel: link dials, `phoneLabel`
 * is what a human reads. Leaving `phone` blank hides the call button rather
 * than shipping a dead link.
 * ------------------------------------------------------------------------- */
const CONTACT = {
  contactUrl: 'https://digitalgeniussolution.com/contact-us/',
  phone: '+917009593522',
  phoneLabel: '+91 70095 93522',
  // The only other pages that actually exist. Everything else the footer used
  // to link to (/docs/, /security/, /compare/*, /privacy/) was never built —
  // those links all landed on NotFound, so they now point at the section of
  // this page that carries the same content.
  aboutUrl: 'https://digitalgeniussolution.com/about-us/',
  blogUrl: 'https://digitalgeniussolution.com/blog/',
};

const TRIAL_POINTS = ['7-day trial', 'No credit card', 'Cancel anytime'];

const NAV_LINKS = [
  ['Features', '#features'],
  ['Tracker', '#tracker'],
  ['Screens', '#screens'],
  ['Pricing', '#pricing'],
  ['Self-hosted', '#deployment'],
  ['White label', '#partners'],
  ['FAQ', '#faq'],
];

const HERO_TICKS = [
  '7-day trial',
  'No credit card',
  'Windows · macOS · Ubuntu',
  'CSV export for payroll',
  'No keylogging, ever',
];

/* ----------------------------------------------------------- deployment tabs
 * The signature element of the page: one product, three ways to run it. All
 * three panels stay mounted (just hidden) rather than lazily rendered, so a
 * crawler — and a keyboard user tabbing through — sees every deployment model
 * without needing to click first. */
const DEPLOY_TABS = [
  { id: 'cloud', icon: Cloud, kicker: '01 / Cloud', name: 'Our cloud', sub: 'Live today, per user' },
  { id: 'server', icon: Server, kicker: '02 / Self-hosted', name: 'Your server', sub: 'Your disk, flat licence' },
  { id: 'brand', icon: Building2, kicker: '03 / OEM', name: 'Your brand', sub: 'Resell it as your own' },
];

const PILLARS = [
  {
    id: 'attendance',
    icon: CalendarDays,
    num: '01 / TIME OFF',
    title: 'Attendance and leave',
    items: [
      ['Shifts that fit your team.', 'Own start time, grace window and late policy per employee, so no rule has to fit everyone.'],
      ['Lunch breaks deducted automatically', 'and kept out of the paid hours.'],
      ['Every leave type.', 'Sick, casual, paid, work-from-home, half-day and short leave, approved or rejected in one click with the whole history kept.'],
      ['Balances that accrue monthly', 'on their own, with no spreadsheet to maintain.'],
      ['One holiday calendar', 'everyone sees, so attendance stops flagging days nobody was meant to work.'],
    ],
  },
  {
    id: 'projects',
    icon: FolderKanban,
    num: '02 / OUTPUT',
    title: 'Projects, tasks and daily reports',
    items: [
      ['Projects with a code, a manager and members.', 'Tasks carry an assignee, a priority and a completion percentage.'],
      ['Progress is a number,', 'not a feeling in a status call.'],
      ['Daily work reports', 'written against a project, with hours and completion, read in one screen instead of asked for.'],
      ['Assignments and approvals reach people', 'through in-app alerts and web push, even when the portal is closed.'],
    ],
  },
  {
    id: 'desktop-tracker',
    icon: MonitorPlay,
    num: '03 / MEASURED TIME',
    title: 'Desktop tracker',
    items: [
      ['A native app, not a browser tab', 'that closes by accident. Windows, macOS and Ubuntu, same app and same account.'],
      ['Active and idle time', 'measured from real keyboard and mouse activity, not from the app simply being open.'],
      ['Screenshots at an unpredictable gap,', 'redrawn each time and capped at fifteen minutes.'],
      ['Offline-safe.', 'Keeps recording without a connection, uploads the backlog later, updates itself when you publish a build.'],
    ],
  },
  {
    id: 'reports',
    icon: BarChart3,
    num: '04 / CONTROL',
    title: 'Reports, roles and retention',
    items: [
      ['Attendance, tracked time and work reports', 'over any date range, on screen as charts and out as CSV for payroll or your accountant.'],
      ['Retention you set.', 'Screenshots delete themselves after the number of days your policy allows.'],
      ['An audit log of who looked at what,', 'including which manager opened whose screenshots.'],
      ['Roles, SSO and directory sync.', 'SAML, OIDC and Active Directory on self-hosted and enterprise plans.'],
      ['REST API and webhooks', 'to push attendance into the payroll system you already run.'],
    ],
  },
];

const RECORDS = [
  ['Active and idle time,', 'from real keyboard and mouse activity'],
  ['The application or window title in use,', 'sampled through the day'],
  ['Screenshots at a random interval you bound,', 'each tagged with the activity percentage'],
  ['Keystroke and click counts,', 'as a measure of activity only'],
  ['Start and stop times,', 'signed in with the same portal account'],
];

const NEVER = [
  ['Record what you type.', 'The content of what is typed never leaves the machine'],
  ['Touch the camera or the microphone', ''],
  ['Run after the employee has stopped it,', 'or while the machine is asleep'],
  ['Hide.', 'The tray icon stays visible the whole time it is recording'],
  ['Read personal files,', 'browser history or messages'],
];

const TIERS = [
  {
    tag: 'Cloud',
    name: 'Starter',
    price: '₹59',
    unit: '/ user / month',
    features: ['Attendance with shifts and grace', 'Leave types, balances and approvals', 'Holiday calendar', 'Web portal, mobile ready', 'CSV export'],
    best: false,
  },
  {
    tag: 'Cloud · most chosen',
    name: 'Growth',
    price: '₹99',
    unit: '/ user / month',
    features: ['Everything in Starter', 'Projects and tasks', 'Daily work reports', 'Desktop time tracker', 'Team chat and notifications'],
    best: true,
  },
  {
    tag: 'Cloud',
    name: 'Pro',
    price: '₹149',
    unit: '/ user / month',
    features: ['Everything in Growth', 'Screenshots with context', 'Live tracker board', 'Audit log and retention rules', 'REST API and webhooks'],
    best: false,
  },
];

const SELF_HOSTED_ROWS = [
  ['Up to 25', 'Full product, updates, email support', '₹40,000'],
  ['Up to 50', 'Full product, updates, email support', '₹70,000'],
  ['Up to 100', 'Adds SSO, directory sync, audit log', '₹1,20,000'],
  ['Up to 250', 'Adds priority support and upgrade assistance', '₹2,25,000'],
  ['250+', 'Adds source-code escrow and a named contact', '₹3,50,000+'],
];

const WHITE_LABEL_POINTS = [
  'Your logo, colours, domain, login page and email templates on every tenant',
  'Desktop builds signed under your publisher name for Windows and macOS',
  'One console to add clients, set seat limits and see usage across all of them',
  'Your landed cost is roughly 40% of our direct price, so the margin is real',
  'Lead registration, an NFR licence for your own team, and a deck you can send',
];

const WHITE_LABEL_PANEL = [
  ['Product name', 'Acme People'],
  ['Portal domain', 'hr.acme.co.in'],
  ['Desktop publisher', 'Acme Technologies'],
  ['Sender address', 'alerts@acme.co.in'],
  ['Employee Desk shown', 'Nowhere'],
];

const SPECS = [
  ['Install', 'Docker Compose, single host'],
  // Was 'App + MySQL 8 + Redis'. The backend is Mongoose 8 on MongoDB
  // (backend/config/db.js), and there is no Redis in it at all.
  ['Stack', 'App + MongoDB'],
  ['Minimum server', '4 vCPU · 8 GB RAM · 200 GB'],
  ['Ports', '443 inbound only'],
  ['Data leaving your network', 'Licence check, or none'],
  ['Screenshot storage', 'Local disk or your S3 bucket'],
  ['Authentication', 'SAML, OIDC, AD/LDAP sync'],
  ['Desktop clients', 'Windows 10/11 · macOS 11+ · Ubuntu 20.04+'],
];

const FAQ = [
  {
    q: 'Can we host it on our own server?',
    a: 'Yes. The self-hosted licence ships as a Docker Compose stack you run on your own server or private cloud. Attendance records, work reports and screenshots stay on your disk. The only outbound call is a licence check, and an offline licence file is available for air-gapped networks.',
  },
  {
    q: 'Are keystrokes logged?',
    a: 'No. Key presses and mouse clicks are counted to measure activity, but what is typed is never captured or transmitted. There is no keylogger in the product.',
  },
  {
    q: 'Can employees see what is being recorded about them?',
    a: 'Yes, and that is deliberate. Every employee gets a notification the moment a screenshot is taken and can open the same gallery their manager sees. Nothing is collected quietly.',
  },
  {
    q: 'Does the desktop tracker work on Mac and Linux?',
    a: 'Yes. The tracker ships for Windows 10 and 11, macOS 11 and later, and Ubuntu 20.04 and later on an Xorg session. It is the same app and the same account on all three.',
  },
  {
    q: 'How often are screenshots taken?',
    a: 'At a random gap between a minimum and a maximum you set, from two to ten minutes by default, and never more than fifteen minutes apart. The gap is redrawn every time so it cannot be predicted or waited out.',
  },
  {
    q: 'Does it work without an internet connection?',
    a: 'The desktop app keeps recording offline and queues everything locally. When the connection returns it uploads the backlog, so a dropped Wi-Fi connection does not leave a hole in the day.',
  },
  {
    q: 'Can we sell this under our own brand?',
    a: 'Yes. The white-label licence covers your logo, colours, domain, email templates and login page, plus signed desktop builds under your publisher name. Partners get a seat pool to resell from and a lead registration programme.',
  },
  {
    q: 'Can we use attendance and leave without the tracker?',
    a: 'Yes. The web portal is complete on its own for attendance, leave, holidays, projects, tasks, work reports, chat and reports. The desktop tracker is an optional add-on you roll out only when it makes sense.',
  },
];

const FOOTER_COLUMNS = [
  {
    title: 'Product',
    links: [
      ['Attendance and leave', '#attendance'],
      ['Projects and tasks', '#projects'],
      ['Desktop time tracker', '#desktop-tracker'],
      ['Reports and retention', '#reports'],
      ['Product screens', '#screens'],
      ['Pricing', '#pricing'],
    ],
  },
  {
    title: 'Deployment',
    links: [
      ['Cloud, self-hosted or OEM', '#deployment'],
      ['White label and partners', '#partners'],
      ['Server specs and install', '#specs'],
      ['What the tracker records', '#tracker'],
      ['Frequently asked', '#faq'],
    ],
  },
  {
    title: 'Company',
    links: [
      ['About Digital Genius Solution', CONTACT.aboutUrl],
      ['Blog', CONTACT.blogUrl],
      ['Contact sales', CONTACT.contactUrl],
      ['Sign in', '/login'],
    ],
  },
];

// Split by who is looking at the screen. A buyer is two people at once — the
// manager who wants the reporting and the employee who has to live in it every
// day — and the second one decides whether the rollout survives.
//
// NOTE: this showcase (and its screenshots) is intentionally left as-is —
// do not change the shots, captions or roles below.
const SHOT_GROUPS = [
  {
    role: 'Admin',
    shots: [
      { key: 'admin-dashboard', label: 'Overview', caption: 'Headcount, attendance, leaves and task health for the whole company on one screen.' },
      { key: 'tracker-live', label: 'Live tracker', caption: 'Who is working right now, on what machine, and how active the day has been so far.' },
      { key: 'screenshots', label: 'Screenshots', caption: 'Every capture with its activity percentage and the app in use, filterable by employee and date range.' },
      { key: 'attendance', label: 'Attendance', caption: 'The whole team’s month at a glance, with late arrivals and leave already marked.' },
      { key: 'reports', label: 'Reports', caption: 'Attendance and tracked time over any range, on screen and exportable as CSV.' },
      { key: 'employees', label: 'Employees', caption: 'The team directory, with each person’s shift policy and leave balance.' },
      { key: 'leaves', label: 'Leave requests', caption: 'Pending requests with balance and history, approved or rejected in one click.' },
      { key: 'projects', label: 'Projects', caption: 'Projects with their manager, members and live completion.' },
    ],
  },
  {
    role: 'Employee',
    shots: [
      { key: 'employee-dashboard', label: 'My day', caption: 'Check in, take lunch, check out — with hours, leave balance and open tasks all on the same card.' },
      { key: 'employee-attendance', label: 'My attendance', caption: 'Every day of the month, with hours worked and late arrivals visible to the employee too.' },
      { key: 'employee-leaves', label: 'My leaves', caption: 'Balance, history and a new request in a few clicks — no form to email anyone.' },
      { key: 'employee-tasks', label: 'My tasks', caption: 'What is assigned, what it belongs to, and how far along it is.' },
      { key: 'employee-work-reports', label: 'Daily work', caption: 'The end-of-day report, written against a project, with hours and completion.' },
    ],
  },
];

const ALL_SHOTS = SHOT_GROUPS.flatMap((g) => g.shots.map((s) => ({ ...s, role: g.role })));

/* ------------------------------------------------------------ anchor scroll */

// The nav is sticky and 4rem tall, so a section that lands flush with the top of
// the viewport lands underneath it. Every jump on this page stops NAV_OFFSET
// short instead. Same number as the scroll-padding-top on html[data-landing] in
// index.css — change both together.
const NAV_OFFSET = 88; // 5.5rem

const glide = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

// Positioning is done by hand rather than handed to the browser's own fragment
// jump. The browser only applies scroll-padding when it owns the scroll, and it
// re-applies a fragment at moments we do not control — a reload, a Back, the
// webfont swapping mid-jump — each of which can leave a heading half hidden
// under the bar. One function, one answer, on every path.
function scrollToHash(hash, behavior) {
  if (!hash || hash === '#' || hash === '#top') {
    window.scrollTo({ top: 0, behavior });
    return true;
  }
  let el = null;
  try {
    el = document.querySelector(hash);
  } catch {
    return false; // a hash that is not a valid selector
  }
  if (!el) return false;
  const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
  window.scrollTo({ top: Math.max(0, top), behavior });
  return true;
}

/* --------------------------------------------------------------------------- */

export default function Landing() {
  const { isAuthenticated, isAdmin } = useAuth();

  // The crawler-facing tags live in index.html, which is what social scrapers and
  // most bots read. This keeps the tab title honest when the route is reached by
  // client-side navigation from inside the app.
  useEffect(() => {
    const prev = document.title;
    document.title = 'Employee Attendance & Work Tracking Software | Cloud or Self-Hosted';
    return () => {
      document.title = prev;
    };
  }, []);

  // Anchor behaviour, scoped to this page — index.css hangs the attribute off the
  // document so nothing leaks into the app.
  //
  // Three separate things happen here:
  //
  // 1. Opening /#tracker directly does nothing on its own. The browser looks for
  //    the anchor while parsing the HTML, and in a SPA nothing has rendered yet,
  //    so the jump has to be repeated once the sections exist — and repeated
  //    again once the webfont has swapped, because that moves every section down.
  //
  // 2. Scroll restoration is taken off the browser. Chrome records a history
  //    entry's scroll offset while a smooth scroll is still animating, so Back
  //    after a nav click restores a position from the middle of that glide: the
  //    reader ends up near the top of the page with the hero heading cut in half
  //    behind the sticky bar. Every entry here is described by its fragment, so
  //    the offset is recomputed from that instead of being replayed.
  //
  // 3. popstate re-positions rather than trusting whatever offset was saved.
  useEffect(() => {
    const html = document.documentElement;
    html.dataset.landing = '';

    const prevRestoration = window.history.scrollRestoration;
    if (prevRestoration) window.history.scrollRestoration = 'manual';

    const { hash } = window.location;
    const deepLink = hash.length > 1;

    // Deep links land instantly — animating through the whole hero on arrival
    // reads as a broken page. Only a click the reader chose to make glides.
    let landedAt = null;
    const land = () => {
      // Once they have scrolled themselves, yanking them back to somewhere they
      // never asked to be is worse than a few pixels of drift.
      if (landedAt !== null && Math.abs(window.scrollY - landedAt) > 4) return;
      if (scrollToHash(hash, 'auto')) landedAt = Math.round(window.scrollY);
    };

    let raf = 0;
    let settle = 0;
    if (deepLink) {
      raf = requestAnimationFrame(() => requestAnimationFrame(land));
      settle = setTimeout(land, 800);
      document.fonts?.ready?.then(land).catch(() => {});
    }

    const onPop = () => {
      const h = window.location.hash;
      if (h.length > 1) scrollToHash(h, 'auto');
      else window.scrollTo({ top: 0, behavior: 'auto' });
    };
    window.addEventListener('popstate', onPop);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      window.removeEventListener('popstate', onPop);
      if (prevRestoration) window.history.scrollRestoration = prevRestoration;
      delete html.dataset.landing;
    };
  }, []);

  // One handler for every in-page link on the page — nav, hero and footer alike —
  // so a section always stops clear of the sticky bar, and the logo goes to a
  // true zero rather than to wherever the last fragment left the page.
  const onAnchorClick = (e) => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // open-in-new-tab etc.
    const el = e.target instanceof Element ? e.target.closest('a[href]') : null;
    const href = el?.getAttribute('href');
    if (!href || !href.startsWith('#')) return;

    e.preventDefault();
    const behavior = glide();

    if (href === '#top') {
      window.scrollTo({ top: 0, behavior });
      // Drop the fragment. Left in place, a reload — or the browser's own retry
      // once the fonts settle — drags the reader straight back down the page.
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return;
    }
    if (scrollToHash(href, behavior)) window.history.pushState(null, '', href);
  };

  const appHome = isAdmin ? '/admin' : '/dashboard';

  return (
    <div
      className="min-h-screen bg-white dark:bg-slate-950 text-[#0B1020] dark:text-slate-100"
      onClick={onAnchorClick}
    >
      <Nav isAuthenticated={isAuthenticated} appHome={appHome} />

      {/* ------------------------------------------------------------- hero */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-50 via-white to-white dark:from-slate-900 dark:via-slate-950 dark:to-slate-950" />
        <div className="absolute -top-40 -right-32 w-[38rem] h-[38rem] rounded-full bg-brand-200/40 dark:bg-brand-800/20 blur-3xl" />
        <div className="absolute -bottom-52 -left-40 w-[34rem] h-[34rem] rounded-full bg-[#E8A317]/20 dark:bg-[#E8A317]/10 blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-5 pt-14 pb-16 sm:pt-20 sm:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="badge gap-2 uppercase tracking-widest font-mono text-[0.7rem] text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
              <span>Attendance</span> <span>·</span> <span>Leave</span> <span>·</span> <span>Work tracking</span>
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.4rem] font-bold leading-[1.1] tracking-tight">
              Employee attendance and work tracking —{' '}
              <span className="text-brand-600 dark:text-brand-400">on our cloud or your server</span>
            </h1>
            <p className="mt-5 text-lg text-[#3A4363] dark:text-slate-300 leading-relaxed">
              One portal for attendance, leave, holidays, projects, daily reports and a
              transparent desktop time tracker. Run it on our cloud in ten minutes, or install it
              on your own server so the screenshots and records never leave your network.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={CONTACT.contactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-base px-6 py-3"
              >
                Start free trial
              </a>
              <a
                href={CONTACT.contactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-base px-6 py-3"
              >
                Open the live demo
              </a>
              {CONTACT.phone && (
                <a href={`tel:${CONTACT.phone}`} className="btn-secondary text-base px-6 py-3">
                  <Phone size={18} /> {CONTACT.phoneLabel || 'Call us'}
                </a>
              )}
            </div>

            <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#6B7391] dark:text-slate-400">
              {HERO_TICKS.map((t) => (
                <li key={t} className="inline-flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-[#0E9F6E]/15"><Check strokeWidth={3} size={12} className="text-[#0E9F6E]" /></span> {t}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* --------------------------------------------------- deployment selector
              The signature element: one product, three ways to run it. All three
              panels stay in the markup (just hidden) so the choice is a click, not
              a page load, and every model is still readable without clicking. */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-12"
            id="deployment"
          >
            <DeploySelector />
          </motion.div>

          {/* The showcase IS the hero image. Repeating the dashboard shot above it
              made the page look like it was showing the same screenshot twice. */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
            className="mt-14"
            id="screens"
          >
            <ShotShowcase />
          </motion.div>
        </div>
      </header>

      {/* -------------------------------------------------------- features */}
      <Section
        id="features"
        eyebrow="What's inside"
        title="Four things your operations team keeps asking for"
        subtitle="Everything runs from one portal and one employee record, so attendance, leave, work and payroll data never have to be reconciled across tools."
        tone="muted"
      >
        <div className="grid md:grid-cols-2 gap-5">
          {PILLARS.map((p) => (
            <article key={p.title} id={p.id} className="card p-7">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 grid place-items-center shrink-0">
                  <p.icon size={18} />
                </div>
                <span className="font-mono text-[0.68rem] tracking-widest uppercase text-brand-600 dark:text-brand-400">
                  {p.num}
                </span>
              </div>
              <h3 className="font-semibold text-lg mt-3 mb-1">{p.title}</h3>
              <ul className="mt-3">
                {p.items.map(([lead, rest]) => (
                  <li
                    key={lead}
                    className="text-sm text-[#3A4363] dark:text-slate-300 py-2.5 border-t border-[#E3E7F5] dark:border-slate-800 first:border-t-0"
                  >
                    <span className="font-medium text-[#0B1020] dark:text-slate-100">{lead}</span> {rest}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </Section>

      {/* ------------------------------------------------------ the tracker */}
      <section id="tracker" className="bg-[#0E1738] text-white">
        <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20">
          <div className="max-w-2xl mb-10">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#8FA6FF] mb-2.5">
              The desktop tracker
            </div>
            <h2 className="text-3xl sm:text-[2.1rem] font-bold tracking-tight leading-tight">
              Work time, measured — not guessed
            </h2>
            <p className="mt-3 text-white/70">
              Monitoring software has a reputation, most of it earned. Here is the full list of
              what this app records and what it will never touch, published so your team can read
              it before you roll it out.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl p-6 bg-white/[0.055] border border-white/[0.14]">
              <h3 className="font-mono text-xs tracking-widest uppercase text-brand-300 mb-4">
                What it records
              </h3>
              <ul>
                {RECORDS.map(([lead, rest]) => (
                  <li key={lead} className="flex gap-3 text-sm text-white/80 py-2.5 border-t border-white/[0.09] first:border-t-0">
                    <Check size={16} className="text-brand-300 shrink-0 mt-0.5" />
                    <span>
                      {lead} {rest}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl p-6 bg-[#E8A317]/[0.09] border border-[#E8A317]/30">
              <h3 className="font-mono text-xs tracking-widest uppercase text-[#E8A317] mb-4">
                What it never does
              </h3>
              <ul>
                {NEVER.map(([lead, rest]) => (
                  <li key={lead} className="flex gap-3 text-sm text-white/80 py-2.5 border-t border-white/[0.09] first:border-t-0">
                    <X size={16} className="text-[#E8A317] shrink-0 mt-0.5" />
                    <span>
                      {lead} {rest}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-6 text-sm text-white/60 max-w-3xl">
            Employees see every screenshot taken of their own screen, in the same gallery their
            manager uses, and get a notification each time one is captured. Monitoring people
            without telling them is how teams lose trust, so the product will not do it.
          </p>
        </div>
      </section>

      {/* ------------------------------------------------------------ pricing */}
      <Section
        id="pricing"
        eyebrow="Pricing"
        title="Published prices, because you should not have to book a call to get one"
        subtitle="Cloud is billed per user per month with a minimum of ten users. Self-hosted is a flat annual licence by headcount band, so the price stops climbing every time you hire."
      >
        <div className="grid md:grid-cols-3 gap-5">
          {TIERS.map((t) => (
            <div
              key={t.name}
              className={`card p-7 flex flex-col ${t.best ? 'ring-4 ring-brand-600/10 border-brand-600' : ''}`}
            >
              <span className="font-mono text-[0.66rem] tracking-widest uppercase text-brand-600 dark:text-brand-400">
                {t.tag}
              </span>
              <h3 className="text-xl font-semibold mt-2 mb-1">{t.name}</h3>
              <p className="font-mono text-3xl font-semibold tracking-tight mt-2">
                {t.price}
                <span className="text-xs font-normal text-[#6B7391] dark:text-slate-500 block mt-1 tracking-normal">
                  {t.unit}
                </span>
              </p>
              <ul className="mt-4 mb-6 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="text-sm text-[#3A4363] dark:text-slate-300 py-1.5">
                    – {f}
                  </li>
                ))}
              </ul>
              <a
                href={CONTACT.contactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={t.best ? 'btn-primary justify-center' : 'btn-secondary justify-center'}
              >
                Start free trial
              </a>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-[#E3E7F5] dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
          <table className="w-full min-w-[520px] border-collapse">
            <caption className="text-left px-6 pt-5 pb-0 font-mono text-[0.7rem] tracking-widest uppercase text-[#6B7391]">
              Self-hosted — annual licence, runs on your server
            </caption>
            <thead>
              <tr>
                {['Employees', "What's included", 'Per year'].map((h) => (
                  <th key={h} className="text-left px-6 pt-5 pb-3 font-mono text-[0.68rem] tracking-widest uppercase text-[#6B7391]">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {SELF_HOSTED_ROWS.map(([emp, inc, price]) => (
                <tr key={emp} className="border-t border-[#E3E7F5] dark:border-slate-800">
                  <th scope="row" className="text-left px-6 py-3.5 text-sm font-semibold">
                    {emp}
                  </th>
                  <td className="px-6 py-3.5 text-sm text-[#3A4363] dark:text-slate-300">{inc}</td>
                  <td className="px-6 py-3.5 text-sm font-mono font-medium">{price}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm text-[#6B7391] dark:text-slate-400">
          One-time setup and handover: ₹25,000. Perpetual licence available at 2.5× the annual
          price with one year of updates, then 18% AMC. Prefer the data separation without
          running a server?{' '}
          <a
            href={CONTACT.contactUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 dark:text-brand-400 hover:underline"
          >
            Ask us about private cloud
          </a>{' '}
          — a single-tenant instance in your own region that we operate.
        </p>
      </Section>

      {/* --------------------------------------------------------- white label */}
      <section id="partners" className="bg-[#0E1738] text-white">
        <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[#8FA6FF] mb-2.5">
              Partners
            </div>
            <h2 className="text-3xl sm:text-[2.1rem] font-bold tracking-tight leading-tight">
              Sell it as your own product
            </h2>
            <p className="mt-3 text-white/70 max-w-xl">
              If you already run IT, payroll or HR for a set of clients, this is a second line of
              recurring revenue that needs no engineering team behind it.
            </p>
            <ul className="mt-6 space-y-0">
              {WHITE_LABEL_POINTS.map((pt) => (
                <li key={pt} className="flex gap-3 text-sm text-white/80 py-2.5 pl-0 border-t border-white/10 first:border-t-0">
                  <ArrowRight size={15} className="text-[#E8A317] shrink-0 mt-0.5" />
                  <span>{pt}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href={CONTACT.contactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn bg-white text-[#0E1738] hover:bg-slate-100 px-5 py-2.5"
              >
                Partner programme
              </a>
              <a
                href={CONTACT.contactUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn bg-transparent text-white ring-1 ring-inset ring-white/30 hover:bg-white/10 px-5 py-2.5"
              >
                Book a walkthrough
              </a>
            </div>
          </div>

          <div className="rounded-2xl bg-white/5 border border-white/15 p-6 font-mono text-sm text-white/75">
            {WHITE_LABEL_PANEL.map(([label, val]) => (
              <div key={label} className="flex justify-between gap-4 py-2.5 border-t border-white/[0.09] first:border-t-0">
                <span>{label}</span>
                <b className={`font-medium ${label === 'Employee Desk shown' ? 'text-[#E8A317]' : 'text-white'}`}>{val}</b>
              </div>
            ))}
            <div className="flex justify-between gap-4 py-2.5 border-t border-white/[0.09]">
              <span>Brand colours</span>
              <span className="flex gap-1.5">
                <i className="w-4 h-4 rounded border border-white/25 block" style={{ background: '#E8A317' }} />
                <i className="w-4 h-4 rounded border border-white/25 block" style={{ background: '#0E9F6E' }} />
                <i className="w-4 h-4 rounded border border-white/25 block" style={{ background: '#fff' }} />
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------------- specs */}
      <Section
        id="specs"
        eyebrow="For whoever signs off on it"
        title="The technical answers, up front"
        subtitle="Your IT team will ask these before they let you buy. They are published rather than emailed on request."
        tone="muted"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SPECS.map(([label, val]) => (
            <div key={label} className="card p-5">
              <dt className="font-mono text-[0.65rem] tracking-widest uppercase text-[#6B7391]">{label}</dt>
              <dd className="mt-2 text-sm font-medium">{val}</dd>
            </div>
          ))}
        </div>
        <pre className="mt-6 rounded-xl bg-[#0E1738] text-[#C7D2FF] text-sm px-5 py-4 overflow-x-auto font-mono">
          <span className="text-[#6D7CB8]"># install, on your own server</span>{'\n'}curl -fsSL https://get.employeedeskcrm.com | sh &amp;&amp; edcrm up
        </pre>
        <p className="mt-4 text-sm text-[#6B7391] dark:text-slate-400">
          Sizing, backup and upgrade notes come with the licence. What the tracker does and
          does not record is listed in full{' '}
          <a href="#tracker" className="text-brand-600 dark:text-brand-400 hover:underline">further up this page</a>.
          For the data processing agreement, retention controls and our DPDP and GDPR position,{' '}
          <a
            href={CONTACT.contactUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 dark:text-brand-400 hover:underline"
          >
            get in touch
          </a>.
        </p>
      </Section>

      {/* -------------------------------------------------------------- faq */}
      <Section id="faq" eyebrow="Questions" title="Frequently asked">
        <div className="grid md:grid-cols-2 gap-5">
          {FAQ.map((f) => (
            <div key={f.q} className="card p-6">
              <h3 className="font-semibold mb-2">{f.q}</h3>
              <p className="text-sm text-[#3A4363] dark:text-slate-300 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ----------------------------------------------------------- contact */}
      <section id="contact" className="relative overflow-hidden bg-gradient-to-br from-brand-600 via-[#1B37B8] to-[#16267A]">
        <div className="absolute -top-24 -right-16 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-5 py-20 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Try it on your own team this week</h2>
          <p className="mt-4 text-white/80 text-lg max-w-2xl mx-auto">
            Start on the cloud in ten minutes, or tell us your headcount and where the server has
            to live and we will price the self-hosted licence on the call.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={CONTACT.contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn bg-white text-[#0E1738] hover:bg-slate-100 text-base px-6 py-3"
            >
              <ExternalLink size={18} /> Start free trial
            </a>
            <a
              href={CONTACT.contactUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn bg-white/10 text-white ring-1 ring-inset ring-white/30 hover:bg-white/20 text-base px-6 py-3"
            >
              Open the live demo
            </a>
            {CONTACT.phone && (
              <a
                href={`tel:${CONTACT.phone}`}
                className="btn bg-white/10 text-white ring-1 ring-inset ring-white/30 hover:bg-white/20 text-base px-6 py-3"
              >
                <Phone size={18} /> {CONTACT.phoneLabel || CONTACT.phone}
              </a>
            )}
          </div>

          <ul className="mt-6 flex flex-wrap justify-center gap-x-7 gap-y-2 text-[0.95rem] font-medium text-white/90">
            {TRIAL_POINTS.map((t) => (
              <li key={t} className="inline-flex items-center gap-2">
                <Check size={18} strokeWidth={2.5} className="shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ------------------------------------------------------------- components */

function Nav({ isAuthenticated, appHome }) {
  return (
    <nav className="sticky top-0 z-40 bg-white/90 dark:bg-slate-950/85 backdrop-blur border-b border-[#E3E7F5] dark:border-slate-800">
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center gap-3">
        <a href="#top" className="flex items-center gap-2.5 min-w-0 shrink-0">
          <img src="/fevicon.svg" alt="" className="h-9 w-9 object-contain" />
          <span className="uppercase leading-[1.05]">
            <span className="block text-[13px] font-extrabold tracking-wider text-orange-500">Employee</span>
            <span className="block text-[10px] whitespace-nowrap">
              <span className="font-medium tracking-[0.3em] text-[#6B7391] dark:text-slate-300">Desk</span>{' '}
              <span className="font-extrabold tracking-[0.3em] text-sky-500">CRM</span>
            </span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-1 ml-6">
          {NAV_LINKS.map(([label, href]) => (
            <a
              key={href}
              href={href}
              className="px-3 py-2 rounded-lg text-sm font-medium text-[#3A4363] dark:text-slate-300 hover:text-brand-600 hover:bg-[#F4F6FC] dark:hover:bg-slate-800"
            >
              {label}
            </a>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Link to={isAuthenticated ? appHome : '/login'} className="btn-ghost hidden sm:inline-flex">
            {isAuthenticated ? 'Dashboard' : 'Sign in'}
          </Link>
          <a
            href={CONTACT.contactUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Start free trial
          </a>
        </div>
      </div>
    </nav>
  );
}

// One product, three ways to run it. Tabs switch which panel (copy + price +
// diagram) is visible; all three stay mounted so nothing here depends on JS
// having already fired to be legible.
function DeploySelector() {
  const [active, setActive] = useState('cloud');

  const PANELS = {
    cloud: {
      heading: 'Signed up before lunch, tracking by the afternoon',
      body: 'We run the servers, the backups and the updates. Import your team, set shifts and leave balances, and share the portal link. Hosted in India, encrypted at rest, daily backups.',
      price: '₹59',
      priceSub: 'per user / month · minimum 10 users · 20% off annual',
      primary: ['Start free trial', CONTACT.contactUrl],
      secondary: ['See all plans', '#pricing'],
      diagram: <CloudDiagram />,
    },
    server: {
      heading: 'Nothing leaves your network, including the screenshots',
      body: 'Ships as a Docker Compose stack. Your server, your database, your disk, your retention policy. The only outbound call is a licence check, and air-gapped sites can use an offline licence file instead. You are the data controller, and there is no sub-processor to declare.',
      price: '₹40,000',
      priceSub: 'per year to 25 employees · one-time setup ₹25,000 · updates and support included',
      primary: ['Talk to us about self-hosting', CONTACT.contactUrl],
      secondary: ['See the server specs', '#specs'],
      diagram: <ServerDiagram />,
    },
    brand: {
      heading: 'Your logo, your domain, your invoice',
      body: 'Built for IT service providers, HR consultants, staffing firms and ERP implementers who already have the client relationship. Every tenant gets your branding across the portal, the login page, the emails and the signed desktop builds. We stay invisible.',
      price: '₹3,00,000',
      priceSub: 'per year · includes a resale seat pool, NFR licence and lead registration',
      primary: ['Partner programme', '#partners'],
      secondary: ['Talk to us', CONTACT.contactUrl],
      diagram: <BrandDiagram />,
    },
  };

  const p = PANELS[active];

  return (
    <div>
      <div role="tablist" aria-label="Choose a deployment model" className="grid sm:grid-cols-3 border border-[#E3E7F5] dark:border-slate-800 rounded-t-xl overflow-hidden">
        {DEPLOY_TABS.map((t) => {
          const on = t.id === active;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setActive(t.id)}
              className={`text-left px-5 py-4 border-r last:border-r-0 sm:border-b-0 border-b border-[#E3E7F5] dark:border-slate-800 transition-colors ${
                on ? 'bg-[#0E1738] text-white' : 'bg-white dark:bg-slate-900 hover:bg-[#F4F6FC] dark:hover:bg-slate-800'
              }`}
            >
              <span className={`flex items-center gap-1.5 font-mono text-[0.65rem] tracking-widest uppercase ${on ? 'text-[#E8A317]' : 'text-[#6B7391]'}`}>
                <t.icon size={12} /> {t.kicker}
              </span>
              <span className="block font-semibold mt-1.5">{t.name}</span>
              <span className={`block text-xs mt-0.5 ${on ? 'text-white/65' : 'text-[#6B7391] dark:text-slate-400'}`}>{t.sub}</span>
            </button>
          );
        })}
      </div>

      <div className="border border-t-0 border-[#E3E7F5] dark:border-slate-800 rounded-b-xl bg-white dark:bg-slate-900 p-6 sm:p-7">
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2 }}
            className="grid lg:grid-cols-2 gap-8 items-center"
          >
            <div>
              <h3 className="text-xl font-semibold mb-2">{p.heading}</h3>
              <p className="text-sm text-[#3A4363] dark:text-slate-300 leading-relaxed">{p.body}</p>
              <p className="font-mono text-base font-semibold mt-4">
                From {p.price}
                <span className="block text-xs font-normal text-[#6B7391] dark:text-slate-400 mt-1 tracking-normal">
                  {p.priceSub}
                </span>
              </p>
              <div className="mt-4 flex flex-wrap gap-2.5">
                <a
                  href={p.primary[1]}
                  target={p.primary[1].startsWith('http') ? '_blank' : undefined}
                  rel={p.primary[1].startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="btn-primary text-sm px-4 py-2"
                >
                  {p.primary[0]}
                </a>
                <a
                  href={p.secondary[1]}
                  target={p.secondary[1].startsWith('http') ? '_blank' : undefined}
                  rel={p.secondary[1].startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="btn-secondary text-sm px-4 py-2"
                >
                  {p.secondary[0]}
                </a>
              </div>
            </div>
            <div className="rounded-xl bg-[#F4F6FC] dark:bg-slate-950 border border-[#E3E7F5] dark:border-slate-800 p-4">
              {p.diagram}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function CloudDiagram() {
  return (
    <svg viewBox="0 0 460 168" role="img" aria-label="Diagram: employee desktop app and web portal send data to Employee Desk Cloud, which we operate." className="w-full h-auto">
      <defs><marker id="a1" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><path d="M0 0l7 3.5L0 7z" fill="#6B7391" /></marker></defs>
      <rect x="6" y="24" width="126" height="52" rx="9" fill="#fff" stroke="#CFD6EE" />
      <text x="20" y="46" fontFamily="ui-monospace,monospace" fontSize="10.5" fill="#0B1020">Desktop app</text>
      <text x="20" y="61" fontFamily="ui-monospace,monospace" fontSize="9" fill="#6B7391">Win · macOS · Ubuntu</text>
      <rect x="6" y="94" width="126" height="46" rx="9" fill="#fff" stroke="#CFD6EE" />
      <text x="20" y="122" fontFamily="ui-monospace,monospace" fontSize="10.5" fill="#0B1020">Web portal</text>
      <path d="M138 50h74" stroke="#6B7391" strokeWidth="1.3" markerEnd="url(#a1)" />
      <path d="M138 117h44c16 0 16-14 16-24v-33" stroke="#6B7391" strokeWidth="1.3" fill="none" markerEnd="url(#a1)" />
      <rect x="222" y="20" width="146" height="126" rx="11" fill="#2349EE" />
      <text x="240" y="52" fontFamily="ui-monospace,monospace" fontSize="10.5" fill="#fff">Employee Desk Cloud</text>
      <text x="240" y="72" fontFamily="ui-monospace,monospace" fontSize="9" fill="#C7D2FF">Mumbai region</text>
      <text x="240" y="88" fontFamily="ui-monospace,monospace" fontSize="9" fill="#C7D2FF">Daily backups</text>
      <text x="240" y="104" fontFamily="ui-monospace,monospace" fontSize="9" fill="#C7D2FF">99.9% uptime</text>
      <text x="240" y="126" fontFamily="ui-monospace,monospace" fontSize="9" fill="#8FA6FF">We operate it</text>
      <rect x="382" y="58" width="72" height="50" rx="9" fill="#F4F6FC" stroke="#CFD6EE" />
      <text x="396" y="80" fontFamily="ui-monospace,monospace" fontSize="9.5" fill="#0B1020">Payroll</text>
      <text x="396" y="94" fontFamily="ui-monospace,monospace" fontSize="9" fill="#6B7391">CSV</text>
      <path d="M370 83h6" stroke="#6B7391" strokeWidth="1.3" markerEnd="url(#a1)" />
    </svg>
  );
}

function ServerDiagram() {
  return (
    <svg viewBox="0 0 460 168" role="img" aria-label="Diagram: the desktop app and web portal talk to a server inside your own network. Only a licence check leaves the building." className="w-full h-auto">
      <defs><marker id="a2" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><path d="M0 0l7 3.5L0 7z" fill="#6B7391" /></marker></defs>
      <rect x="4" y="8" width="352" height="152" rx="12" fill="none" stroke="#0E9F6E" strokeWidth="1.4" strokeDasharray="5 4" />
      <text x="16" y="26" fontFamily="ui-monospace,monospace" fontSize="9" fill="#0E9F6E">YOUR NETWORK</text>
      <rect x="18" y="42" width="118" height="46" rx="9" fill="#fff" stroke="#CFD6EE" />
      <text x="31" y="63" fontFamily="ui-monospace,monospace" fontSize="10.5" fill="#0B1020">Desktop app</text>
      <text x="31" y="77" fontFamily="ui-monospace,monospace" fontSize="9" fill="#6B7391">points at your URL</text>
      <rect x="18" y="100" width="118" height="42" rx="9" fill="#fff" stroke="#CFD6EE" />
      <text x="31" y="126" fontFamily="ui-monospace,monospace" fontSize="10.5" fill="#0B1020">Web portal</text>
      <path d="M142 65h44" stroke="#6B7391" strokeWidth="1.3" markerEnd="url(#a2)" />
      <path d="M142 121h30c14 0 14-12 14-22v-26" stroke="#6B7391" strokeWidth="1.3" fill="none" markerEnd="url(#a2)" />
      <rect x="196" y="38" width="146" height="106" rx="11" fill="#0E1738" />
      <text x="212" y="64" fontFamily="ui-monospace,monospace" fontSize="10.5" fill="#fff">Your server</text>
      <text x="212" y="84" fontFamily="ui-monospace,monospace" fontSize="9" fill="#8FA6FF">App + MongoDB</text>
      <text x="212" y="100" fontFamily="ui-monospace,monospace" fontSize="9" fill="#8FA6FF">Screenshots on disk</text>
      <text x="212" y="116" fontFamily="ui-monospace,monospace" fontSize="9" fill="#8FA6FF">Your backups</text>
      <text x="212" y="134" fontFamily="ui-monospace,monospace" fontSize="9" fill="#E8A317">You operate it</text>
      {/* The network boundary ends at x=356, so both labels are centred out at 388
          rather than started at 352 — otherwise the dashed green edge runs straight
          through "licence" and "check only". textAnchor keeps them clear whichever
          monospace face the browser resolves. */}
      <path d="M348 91h56" stroke="#CFD6EE" strokeWidth="1.3" strokeDasharray="4 4" markerEnd="url(#a2)" />
      <text x="388" y="80" textAnchor="middle" fontFamily="ui-monospace,monospace" fontSize="8.5" fill="#6B7391">licence</text>
      <text x="388" y="112" textAnchor="middle" fontFamily="ui-monospace,monospace" fontSize="8.5" fill="#6B7391">check only</text>
    </svg>
  );
}

function BrandDiagram() {
  return (
    <svg viewBox="0 0 460 168" role="img" aria-label="Diagram: one platform serves several client tenants, each carrying the partner's own branding." className="w-full h-auto">
      <defs><marker id="a3" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><path d="M0 0l7 3.5L0 7z" fill="#6B7391" /></marker></defs>
      <rect x="6" y="52" width="132" height="66" rx="11" fill="#0E1738" />
      <text x="22" y="80" fontFamily="ui-monospace,monospace" fontSize="10.5" fill="#fff">Your platform</text>
      <text x="22" y="97" fontFamily="ui-monospace,monospace" fontSize="9" fill="#E8A317">hr.youragency.com</text>
      <path d="M144 66h44" stroke="#6B7391" strokeWidth="1.3" markerEnd="url(#a3)" />
      <path d="M144 85h44" stroke="#6B7391" strokeWidth="1.3" markerEnd="url(#a3)" />
      <path d="M144 104h44" stroke="#6B7391" strokeWidth="1.3" markerEnd="url(#a3)" />
      <rect x="196" y="20" width="150" height="38" rx="9" fill="#fff" stroke="#CFD6EE" />
      <text x="210" y="44" fontFamily="ui-monospace,monospace" fontSize="10" fill="#0B1020">Client A · 40 seats</text>
      <rect x="196" y="66" width="150" height="38" rx="9" fill="#fff" stroke="#CFD6EE" />
      <text x="210" y="90" fontFamily="ui-monospace,monospace" fontSize="10" fill="#0B1020">Client B · 120 seats</text>
      <rect x="196" y="112" width="150" height="38" rx="9" fill="#fff" stroke="#CFD6EE" />
      <text x="210" y="136" fontFamily="ui-monospace,monospace" fontSize="10" fill="#0B1020">Client C · 25 seats</text>
      <text x="360" y="80" fontFamily="ui-monospace,monospace" fontSize="9" fill="#6B7391">You bill</text>
      <text x="360" y="96" fontFamily="ui-monospace,monospace" fontSize="9" fill="#6B7391">them directly</text>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// LEFT UNCHANGED, as requested: the tabbed admin/employee screenshot
// showcase. Do not edit the shots, captions, roles or this component.
// ---------------------------------------------------------------------------
function ShotShowcase() {
  const [i, setI] = useState(0);
  const active = ALL_SHOTS[i];

  return (
    <div>
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-soft overflow-hidden">
        <div className="h-10 flex items-center gap-1.5 px-4 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          <div className="ml-3 hidden sm:flex items-center h-5 px-2.5 rounded-full bg-white/80 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 min-w-0">
            <span className="text-[10px] text-slate-400 truncate">employeedeskcrm.com</span>
          </div>
          <span className="ml-auto badge bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">
            {active.role} view
          </span>
        </div>

        {/* Fixed 16:9 box: mode="wait" unmounts the old image before the new one
            mounts, and without a reserved height the page would jump each switch. */}
        <div className="relative aspect-video bg-slate-50 dark:bg-slate-950">
          <AnimatePresence mode="wait">
            <motion.img
              key={active.key}
              src={`/shots/${active.key}.png`}
              alt={`Employee Desk CRM — ${active.label}, ${active.role.toLowerCase()} view`}
              width={1440}
              height={810}
              loading={i === 0 ? 'eager' : 'lazy'}
              decoding="async"
              initial={{ opacity: 0, scale: 1.015 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.995 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
          </AnimatePresence>
        </div>
      </div>

      <div className="mt-4 h-10 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.p
            key={active.key}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.22 }}
            className="text-sm  text-slate-600 dark:text-slate-300 max-w-2xl"
          >
            {active.caption}
          </motion.p>
        </AnimatePresence>
      </div>

      <div className="mt-3 space-y-2.5">
        {SHOT_GROUPS.map((g) => (
          <div key={g.role} className="flex flex-wrap items-center gap-2">
            <span className="w-16 shrink-0 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
              {g.role}
            </span>
            {g.shots.map((s) => {
              const idx = ALL_SHOTS.findIndex((x) => x.key === s.key);
              const on = idx === i;
              return (
                <motion.button
                  key={s.key}
                  type="button"
                  onClick={() => setI(idx)}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  aria-pressed={on}
                  className={`relative px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${
                    on
                      ? 'text-white'
                      : 'text-slate-600 dark:text-slate-300 bg-white/80 dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 hover:border-brand-400 hover:text-brand-700 dark:hover:text-brand-300'
                  }`}
                >
                  {/* One pill shared across every tab, so the highlight slides from
                      the old button to the new one instead of blinking. */}
                  {on && (
                    <motion.span
                      layoutId="shot-pill"
                      className="absolute inset-0 rounded-lg bg-brand-600 shadow-sm"
                      transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                    />
                  )}
                  <span className="relative z-10">{s.label}</span>
                </motion.button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ id, eyebrow, title, subtitle, tone, children }) {
  return (
    <section
      id={id}
      className={tone === 'muted' ? 'bg-[#F4F6FC] dark:bg-slate-900/40 border-y border-[#E3E7F5] dark:border-slate-800' : ''}
    >
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-20">
        <div className="max-w-2xl mb-10">
          {eyebrow && (
            <div className="text-xs font-semibold uppercase tracking-widest text-brand-600 dark:text-brand-400 mb-2.5">
              {eyebrow}
            </div>
          )}
          <h2 className="text-3xl sm:text-[2.1rem] font-bold tracking-tight leading-tight">{title}</h2>
          {subtitle && <p className="mt-3 text-[#3A4363] dark:text-slate-300">{subtitle}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}

function FootLink({ href, children }) {
  const cls = 'text-white/70 hover:text-white';
  if (href.startsWith('#')) return <a href={href} className={cls}>{children}</a>;
  if (href.startsWith('/')) return <Link to={href} className={cls}>{children}</Link>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {children}
    </a>
  );
}

function Footer() {
  return (
    <footer className="bg-[#080C1A] text-white/60">
      <div className="max-w-6xl mx-auto px-5 py-12">
        <div className="grid gap-8 lg:grid-cols-[1.6fr_repeat(3,1fr)]">
          <div className="max-w-sm">
            <div className="flex items-center gap-2.5 mb-3">
              <img src="/fevicon.svg" alt="" className="h-8 w-8 object-contain" />
              <span className="uppercase leading-[1.05]">
                <span className="block text-[12px] font-extrabold tracking-wider text-orange-500">Employee</span>
                <span className="block text-[9px]">
                  <span className="font-medium tracking-[0.3em] text-white/60">Desk</span>{' '}
                  <span className="font-extrabold tracking-[0.3em] text-sky-500">CRM</span>
                </span>
              </span>
            </div>
            <p className="text-sm leading-relaxed">
              Employee attendance, leave and work-tracking software for teams that would rather
              read the numbers than chase them.
            </p>
            {CONTACT.phone && (
              <p className="mt-3.5">
                <a href={`tel:${CONTACT.phone}`} className="text-white/70 hover:text-white">
                  {CONTACT.phoneLabel || CONTACT.phone}
                </a>
              </p>
            )}
          </div>

          {FOOTER_COLUMNS.map((col) => (
            <div key={col.title} className="text-sm">
              <div className="font-mono text-[0.68rem] tracking-widest uppercase text-white/40 mb-3.5">
                {col.title}
              </div>
              <ul className="space-y-2">
                {col.links.map(([label, href]) => (
                  <li key={label}>
                    <FootLink href={href}>{label}</FootLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-wrap justify-between gap-4 text-xs text-white/40">
          <span>© {new Date().getFullYear()} Employee Desk CRM. All rights reserved.</span>
        </div>
      </div>
    </footer>
  );
}